import { __rest } from '../../../../../node_modules/tslib/tslib.es6.mjs.js';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Editor } from 'primereact/editor';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { Password } from 'primereact/password';
import { RadioButton } from 'primereact/radiobutton';
import { Rating } from 'primereact/rating';
import { SelectButton } from 'primereact/selectbutton';
import { Slider } from 'primereact/slider';
import { classNames } from 'primereact/utils';
import React, { useMemo, useContext } from 'react';
import { Field } from 'react-final-form';
import { v4 } from 'uuid';
import FormContext from '../../../../api/contexts.js';
import { useAppSelector } from '../../../../hooks/hooks.js';
import { selectMode } from '../../../../store/reducers/enginePropertiesSlice.js';
import C2Calendar from '../C2Calendar/index.js';
import C2RadioButtonGroup from '../C2RadioButtonGroup/C2RadioButtonGroup.js';
import { getSizeClassNamesFromClassName } from '../../../../util.js';

var C2FieldType;
(function (C2FieldType) {
    C2FieldType["Calendar"] = "Calendar";
    C2FieldType["Checkbox"] = "Checkbox";
    C2FieldType["Dropdown"] = "Dropdown";
    C2FieldType["Editor"] = "Editor";
    C2FieldType["InputNumber"] = "InputNumber";
    C2FieldType["InputSwitch"] = "InputSwitch";
    C2FieldType["InputText"] = "InputText";
    C2FieldType["InputTextarea"] = "InputTextarea";
    C2FieldType["MultiSelect"] = "MultiSelect";
    C2FieldType["Password"] = "Password";
    C2FieldType["RadioButton"] = "RadioButton";
    C2FieldType["RadioButtonGroup"] = "RadioButtonGroup";
    C2FieldType["Rating"] = "Rating";
    C2FieldType["SelectButton"] = "SelectButton";
    C2FieldType["Slider"] = "Slider";
})(C2FieldType || (C2FieldType = {}));
// TODO aria labels will be added (hint, error, etc.)
const C2Field = (_a) => {
    var _b;
    var { c2FieldClassName, hint, label, c2FieldType, labelPlacement, id } = _a, otherProps = __rest(_a, ["c2FieldClassName", "hint", "label", "c2FieldType", "labelPlacement", "id"]);
    const inputId = useMemo(() => v4(), []);
    const mode = useAppSelector(selectMode);
    const inputBeforeLabel = c2FieldType === C2FieldType.Checkbox ||
        c2FieldType === C2FieldType.InputSwitch ||
        c2FieldType === C2FieldType.RadioButton;
    const form = useContext(FormContext);
    // TODO: specify width classes
    const elementClassName = mode === "design"
        ? ((_b = otherProps.className) === null || _b === void 0 ? void 0 : _b.split(" ").filter((c) => !c.includes("w-")).join(" ")) +
            (c2FieldType === C2FieldType.Checkbox ||
                c2FieldType === C2FieldType.InputSwitch ||
                c2FieldType === C2FieldType.RadioButton
                ? ""
                : " w-full")
        : otherProps.className;
    const fieldClassName = mode === "live"
        ? classNames(getSizeClassNamesFromClassName(elementClassName !== null && elementClassName !== void 0 ? elementClassName : "", "width"), "h-12", c2FieldClassName)
        : c2FieldClassName !== null && c2FieldClassName !== void 0 ? c2FieldClassName : "";
    const renderC2Field = () => (React.createElement("div", { id: id, className: classNames({
            "field-checkbox": c2FieldType === C2FieldType.Checkbox ||
                c2FieldType === C2FieldType.InputSwitch,
            "field-radiobutton": c2FieldType === C2FieldType.RadioButton,
            field: c2FieldType !== C2FieldType.Checkbox &&
                c2FieldType !== C2FieldType.InputSwitch &&
                c2FieldType !== C2FieldType.RadioButton,
            "flex align-items-center gap-4 p-1": labelPlacement === "horizontal"
        }, fieldClassName, "mb-0") },
        label && !inputBeforeLabel && (React.createElement("label", { className: classNames("block", {
                "mb-0": labelPlacement === "horizontal"
            }), htmlFor: inputId }, label)),
        renderFieldComponent(),
        label && inputBeforeLabel && (React.createElement("label", { className: "block", htmlFor: inputId }, label)),
        hint && React.createElement("small", { className: "block" }, hint)));
    const overrideProps = mode === "design"
        ? {
            disabled: true
        }
        : {};
    const renderFormField = () => {
        const additionalFieldProps = () => {
            switch (c2FieldType) {
                case C2FieldType.Checkbox:
                case C2FieldType.InputSwitch:
                case C2FieldType.SelectButton:
                    return { type: "checkbox" };
                case C2FieldType.RadioButton:
                    return { type: "radio" };
            }
        };
        return (React.createElement(Field, Object.assign({ name: otherProps.name || "" }, additionalFieldProps(), { render: ({ input }) => (React.createElement("div", { id: id, className: classNames({
                    "field-checkbox": c2FieldType === C2FieldType.Checkbox ||
                        c2FieldType === C2FieldType.InputSwitch,
                    "field-radiobutton": c2FieldType === C2FieldType.RadioButton,
                    field: c2FieldType !== C2FieldType.Checkbox &&
                        c2FieldType !== C2FieldType.InputSwitch &&
                        c2FieldType !== C2FieldType.RadioButton,
                    "flex align-items-center gap-4 p-1": labelPlacement === "horizontal"
                }, fieldClassName, "mb-0") },
                label && !inputBeforeLabel && (React.createElement("label", { className: classNames("block", {
                        "mb-0": labelPlacement === "horizontal"
                    }), htmlFor: inputId }, label)),
                renderFieldComponent(Object.assign(Object.assign({}, input), { value: form.initialValue ? form.initialValue[input.name] : "" })),
                label && inputBeforeLabel && (React.createElement("label", { className: "block", htmlFor: inputId }, label)),
                hint && React.createElement("small", { className: "block" }, hint))) })));
    };
    if (form.value) {
        return renderFormField();
    }
    else {
        return renderC2Field();
    }
    function renderFieldComponent(input) {
        var _a;
        const onChangeSet = () => {
            const newEvents = {
                onChange: (e) => {
                    if (c2FieldType === C2FieldType.InputNumber) {
                        if (input === null || input === void 0 ? void 0 : input.onChange)
                            input === null || input === void 0 ? void 0 : input.onChange(e.value);
                        else if (otherProps === null || otherProps === void 0 ? void 0 : otherProps.onChange)
                            otherProps === null || otherProps === void 0 ? void 0 : otherProps.onChange(e.value);
                    }
                    else {
                        if (input === null || input === void 0 ? void 0 : input.onChange)
                            input === null || input === void 0 ? void 0 : input.onChange(e);
                        else if (otherProps === null || otherProps === void 0 ? void 0 : otherProps.onChange)
                            otherProps === null || otherProps === void 0 ? void 0 : otherProps.onChange(e);
                    }
                }
            };
            if (input) {
                let eventName = "onChange";
                switch (c2FieldType) {
                    case C2FieldType.Editor:
                        eventName = "onTextChange";
                        break;
                    default:
                        eventName = "onChange";
                        break;
                }
                newEvents[eventName] = (event) => {
                    let eventType = "onChange";
                    let eventKey = "";
                    switch (c2FieldType) {
                        case C2FieldType.Editor:
                            eventType = "onTextChange";
                            eventKey = "htmlValue";
                            break;
                        case C2FieldType.InputNumber:
                        case C2FieldType.Slider:
                            eventKey = "value";
                            break;
                        case C2FieldType.RadioButton:
                            eventKey = "checked";
                            break;
                        default:
                            eventType = "onChange";
                            break;
                    }
                    if (otherProps === null || otherProps === void 0 ? void 0 : otherProps[eventType]) {
                        if (eventKey)
                            otherProps === null || otherProps === void 0 ? void 0 : otherProps[eventType](event[eventKey]);
                        else
                            otherProps === null || otherProps === void 0 ? void 0 : otherProps[eventType](event);
                    }
                    eventKey ? input.onChange(event[eventKey]) : input.onChange(event);
                };
            }
            return newEvents;
        };
        const newEvents = onChangeSet();
        switch (c2FieldType) {
            case C2FieldType.Calendar:
                let removeValueFromInputs = {};
                if (input) {
                    removeValueFromInputs = (_a = Object.keys(input)) === null || _a === void 0 ? void 0 : _a.filter((item) => item === "value");
                }
                return (React.createElement(C2Calendar, Object.assign({}, otherProps, overrideProps, { className: classNames(elementClassName, "w-full") }, removeValueFromInputs, newEvents, { value: otherProps.value, inputId: inputId })));
            case C2FieldType.Checkbox:
                return (React.createElement(Checkbox, Object.assign({}, otherProps, overrideProps, { className: elementClassName }, input, newEvents, { checked: otherProps.checked, inputId: inputId })));
            case C2FieldType.Dropdown:
                return (React.createElement(Dropdown, Object.assign({}, otherProps, overrideProps, { className: classNames(elementClassName, "w-full") }, input, newEvents, { value: otherProps.value, inputId: inputId })));
            case C2FieldType.Editor:
                return (React.createElement(Editor, Object.assign({}, otherProps, overrideProps, { className: classNames(elementClassName, "w-full flex flex-column h-10") }, input, newEvents, { value: otherProps.value, id: inputId })));
            case C2FieldType.InputNumber: {
                let inputCopy;
                if (input) {
                    inputCopy = Object.assign({}, input);
                    inputCopy.value = otherProps.value;
                }
                return (React.createElement(InputNumber, Object.assign({}, otherProps, overrideProps, { className: elementClassName }, inputCopy, { inputId: inputId }, newEvents)));
            }
            case C2FieldType.InputSwitch:
                return (React.createElement(InputSwitch, Object.assign({}, otherProps, overrideProps, { className: elementClassName }, input, { checked: otherProps.checked, inputId: inputId }, newEvents)));
            case C2FieldType.InputText: {
                let inputCopy;
                if (input) {
                    inputCopy = Object.assign({}, input);
                    inputCopy.value = otherProps.value;
                }
                return (React.createElement(InputText, Object.assign({}, otherProps, overrideProps, { className: classNames(elementClassName, "w-full") }, inputCopy, { id: inputId }, newEvents)));
            }
            case C2FieldType.InputTextarea:
                return (React.createElement(InputTextarea, Object.assign({}, otherProps, overrideProps, { className: classNames(elementClassName, "w-full") }, input, { value: otherProps.value, id: inputId }, newEvents)));
            case C2FieldType.MultiSelect:
                return (React.createElement(MultiSelect, Object.assign({}, otherProps, overrideProps, { inputId: inputId }, input, newEvents, { value: otherProps.value, className: "w-full" })));
            case C2FieldType.Password:
                return (React.createElement(Password, Object.assign({}, otherProps, overrideProps, { className: classNames(elementClassName, "w-full") }, input, { inputId: inputId }, newEvents, { value: otherProps.value, inputClassName: "w-full" })));
            case C2FieldType.RadioButton:
                return (React.createElement(RadioButton, Object.assign({}, otherProps, overrideProps, { className: elementClassName }, input, { inputId: inputId, checked: otherProps.checked }, newEvents)));
            case C2FieldType.RadioButtonGroup:
                return (React.createElement(C2RadioButtonGroup, Object.assign({}, otherProps, overrideProps, { className: elementClassName }, input, { id: inputId }, newEvents, { value: otherProps.value })));
            case C2FieldType.Rating:
                return (React.createElement(Rating, Object.assign({}, otherProps, overrideProps, { className: elementClassName }, input, { id: inputId, value: otherProps.value }, newEvents)));
            case C2FieldType.SelectButton:
                return (React.createElement(SelectButton, Object.assign({}, otherProps, overrideProps, { className: elementClassName }, input, { id: inputId }, newEvents, { value: otherProps.value })));
            case C2FieldType.Slider:
                return (React.createElement(Slider, Object.assign({}, otherProps, overrideProps, { className: classNames(elementClassName, "w-full") }, input, { id: inputId }, newEvents, { value: otherProps.value })));
            default:
                return null;
        }
    }
};

export { C2Field as default };
//# sourceMappingURL=index.js.map
